import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { getEnv } from '../widgets/utils/env';

/**
 * Error class for WAHA API errors
 */
export class WAHAError extends Error {
  status: number;
  data?: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'WAHAError';
    this.status = status;
    this.data = data;
  }
}

/**
 * WAHA API Client
 * Handles requests to the WhatsApp HTTP API server
 */
export class WAHAClient {
  private client: AxiosInstance;
  private apiKey: string;
  
  constructor() {
    const env = getEnv();
    const baseURL = env.VITE_WHATSAPP_SERVER_URL || 'http://localhost:3000';
    this.apiKey = env.VITE_WHATSAPP_SERVER_API_KEY || '';
    
    this.client = axios.create({
      baseURL,
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    // Add request interceptor for auth headers
    this.client.interceptors.request.use((config) => {
      config.headers['X-Api-Key'] = this.apiKey;
      return config;
    });
    
    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const status = error.response?.status || 500;
        const message = (error.response?.data as any)?.message || error.message || 'Unknown error';
        const data = error.response?.data;
        
        throw new WAHAError(message, status, data);
      }
    );
  }
  
  /**
   * Gets session-specific API path
   */
  private getSessionPath(sessionName: string, endpoint: string): string {
    // Ensure endpoint starts with a slash if not already
    if (!endpoint.startsWith('/')) {
      endpoint = '/' + endpoint;
    }
    return `/api/${sessionName}${endpoint}`;
  }
  
  /**
   * Make general API request
   */
  public async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.request(config);
      return response.data;
    } catch (error) {
      if (error instanceof WAHAError) {
        throw error;
      }
      throw new WAHAError(
        error instanceof Error ? error.message : 'Unknown error', 
        500
      );
    }
  }
  
  /**
   * GET request
   */
  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'GET', url });
  }
  
  /**
   * POST request
   */
  public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'POST', url, data });
  }
  
  /**
   * PUT request
   */
  public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }
  
  /**
   * DELETE request
   */
  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }
  
  /**
   * Session-specific methods
   */
  
  /**
   * GET request for a specific session
   */
  public async getSessionResource<T>(sessionName: string, endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const url = this.getSessionPath(sessionName, endpoint);
    return this.get<T>(url, config);
  }
  
  /**
   * POST request for a specific session
   */
  public async postSessionResource<T>(sessionName: string, endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const url = this.getSessionPath(sessionName, endpoint);
    return this.post<T>(url, data, config);
  }
  
  /**
   * PUT request for a specific session
   */
  public async putSessionResource<T>(sessionName: string, endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const url = this.getSessionPath(sessionName, endpoint);
    return this.put<T>(url, data, config);
  }
  
  /**
   * DELETE request for a specific session
   */
  public async deleteSessionResource<T>(sessionName: string, endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const url = this.getSessionPath(sessionName, endpoint);
    return this.delete<T>(url, config);
  }
  
  /**
   * Get data as binary or base64
   */
  public async getBinary(url: string, asBase64: boolean = false): Promise<string | Blob> {
    try {
      if (asBase64) {
        const response = await this.client.get(url, {
          headers: {
            'X-Api-Key': this.apiKey,
            'Accept': 'application/json'
          }
        });
        return `data:${response.data.mimetype};base64,${response.data.data}`;
      } else {
        const response = await this.client.get(url, {
          responseType: 'blob'
        });
        return new Blob([response.data], { type: response.headers['content-type'] });
      }
    } catch (error) {
      if (error instanceof WAHAError) {
        throw error;
      }
      throw new WAHAError(
        error instanceof Error ? error.message : 'Failed to get binary data', 
        500
      );
    }
  }
}

// Create and export a singleton instance
export const wahaClient = new WAHAClient(); 